import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { StdioServerTransport } from "@modelcontextprotocol/sdk/server/stdio.js";
import { z } from "zod";
import { resolveCorpusDir } from "./config.js";
import { CompanionService, type CorpusStatus } from "./service.js";
import { saveConversationSchema } from "./saveSchema.js";
import type { SearchFilters } from "./retriever.js";
import { VERSION } from "./version.js";

// Text-only JSON tool result. Structured enough for the calling LLM to parse,
// while staying a plain MCP text content block.
function jsonResult(payload: unknown) {
  return { content: [{ type: "text" as const, text: JSON.stringify(payload, null, 2) }] };
}

function errorResult(message: string) {
  return { content: [{ type: "text" as const, text: message }], isError: true };
}

/**
 * The freshness block every read tool carries. Trimmed down from the full
 * CorpusStatus: a per-query field is paid for on every call, so it holds only
 * what changes how a result should be read, and drops `staleWarning` entirely
 * when the corpus looks current.
 */
function corpusField(status: CorpusStatus) {
  return {
    lastIngestAt: status.lastIngestAt,
    hoursSinceIngest: status.hoursSinceIngest,
    conversationCount: status.conversationCount,
    ...(status.staleWarning ? { staleWarning: status.staleWarning } : {})
  };
}

// Accept either epoch milliseconds or an ISO-8601 date string.
function parseDate(value: string | undefined): number | undefined {
  if (!value) {
    return undefined;
  }
  if (/^\d+$/.test(value)) {
    return Number(value);
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? undefined : ms;
}

/** Start the MCP stdio server. Exported so `cli.ts`'s `serve` subcommand can call it directly. */
export async function runServe(): Promise<void> {
  const root = resolveCorpusDir(process.env.LLMNESIA_CORPUS_DIR);
  const service = await CompanionService.open(root, {
    // stdout is the MCP JSON-RPC channel, so diagnostics go to stderr.
    onDrainError: (error) =>
      process.stderr.write(
        `llmnesia: inbox drain failed, serving the existing index: ` +
          `${error instanceof Error ? error.message : String(error)}\n`
      )
  });

  const server = new McpServer(
    { name: "llmnesia", version: VERSION },
    {
      instructions:
        "Search and retrieve the user's own past AI conversations (ChatGPT, Claude, " +
        "Gemini, etc.) from their local LLMnesia corpus. Use search_conversations to " +
        "find relevant prior chats before answering from scratch, get_conversation to " +
        "read a full transcript, and list_recent to see what they worked on lately. " +
        "Search and list results carry a `corpus` block saying when the corpus last " +
        "received data; if a chat the user expects is missing, check corpus_status " +
        "before concluding it does not exist."
    }
  );

  server.registerTool(
    "search_conversations",
    {
      title: "Search conversations",
      description:
        "Full-text search across the user's past AI conversations. Returns matching " +
        "conversations with a highlighted snippet, ranked by relevance. Defaults to " +
        "recall-first matching (any term); pass match_mode to answer questions where " +
        "a wrong hit matters, such as whether or how often something was discussed.",
      inputSchema: {
        query: z.string().describe("Search terms (natural words; FTS operators are not needed)."),
        match_mode: z
          .enum(["any", "all", "phrase"])
          .optional()
          .describe(
            "How to combine query terms. 'any' (default) ORs them: highest recall, but " +
              "common words in the query match almost everything, so results cannot be " +
              "counted or used to prove something was discussed. 'all' requires every " +
              "term in the same turn. 'phrase' requires the exact wording, adjacent and " +
              "in order. Use 'all' or 'phrase' for existence and frequency questions, " +
              "and strip filler words from the query first."
          ),
        platform: z
          .string()
          .optional()
          .describe("Restrict to one platform, e.g. 'claude', 'chatgpt', 'gemini'."),
        title: z.string().optional().describe("Only conversations whose title contains this substring."),
        date_from: z.string().optional().describe("Only conversations created on/after this date (ISO or epoch ms)."),
        date_to: z.string().optional().describe("Only conversations created on/before this date (ISO or epoch ms)."),
        limit: z.number().int().min(1).max(50).optional().describe("Max results (default 10).")
      }
    },
    async (args) => {
      const filters: SearchFilters = {
        platform: args.platform,
        title: args.title,
        dateFrom: parseDate(args.date_from),
        dateTo: parseDate(args.date_to),
        limit: args.limit,
        matchMode: args.match_mode
      };
      const hits = await service.search(args.query, filters);
      // search() already drained the inbox; don't make it stat the inbox twice.
      const status = await service.corpusStatus({ refresh: false });
      return jsonResult({
        count: hits.length,
        corpus: corpusField(status),
        results: hits.map((h) => ({
          id: h.docId,
          title: h.title,
          platform: h.platform,
          url: h.url,
          createdAt: h.createdAt,
          score: Number(h.score.toFixed(4)),
          snippet: h.snippet,
          matchedIn: h.matchRole === "title" ? "title" : `${h.matchRole} turn ${h.matchMsgIndex}`,
          pinned: h.pinned,
          messageCount: h.messageCount
        }))
      });
    }
  );

  server.registerTool(
    "get_conversation",
    {
      title: "Get conversation",
      description: "Return the full transcript of one conversation by its id (the `id` from search results).",
      inputSchema: {
        id: z.string().describe("The conversation id (docId), e.g. 'claude:abc-123'.")
      }
    },
    async (args) => {
      const record = await service.getConversation(args.id);
      if (!record) {
        return errorResult(`No conversation found with id '${args.id}'.`);
      }
      return jsonResult({
        conversation: record.conversation,
        messages: record.messages.map((m) => ({
          index: m.msgIndex,
          role: m.role,
          // Prefer the block-formatted copy; fall back to the search-collapsed text.
          text: m.formattedText ?? m.text
        }))
      });
    }
  );

  server.registerTool(
    "list_recent",
    {
      title: "List recent conversations",
      description: "List the user's most recently updated conversations (title + summary), newest first.",
      inputSchema: {
        limit: z.number().int().min(1).max(100).optional().describe("How many to return (default 10).")
      }
    },
    async (args) => {
      const entries = await service.listRecent(args.limit ?? 10);
      const status = await service.corpusStatus({ refresh: false });
      return jsonResult({
        count: entries.length,
        corpus: corpusField(status),
        conversations: entries.map((e) => ({
          id: e.docId,
          title: e.title,
          platform: e.platform,
          updatedAt: e.updatedAt,
          messageCount: e.messageCount,
          pinned: e.pinned,
          summary: e.summary ?? e.preview
        }))
      });
    }
  );

  server.registerTool(
    "corpus_status",
    {
      title: "Corpus status",
      description:
        "Report how current the local corpus is: when browser data last reached it, " +
        "how many conversations it holds, and the newest conversation it knows about. " +
        "Use this when a conversation the user is sure they had cannot be found, to " +
        "tell a genuinely missing chat apart from a corpus that has stopped receiving " +
        "updates from the browser extension.",
      inputSchema: {}
    },
    async () => {
      const status = await service.corpusStatus();
      return jsonResult(status);
    }
  );

  server.registerTool(
    "save_conversation",
    {
      title: "Save conversation",
      description:
        "Add or update a conversation in the user's local corpus. Provide platform, " +
        "title, and the messages; identity is keyed on docId (or platform:sourceId), so " +
        "re-saving the same conversation updates it in place.",
      inputSchema: saveConversationSchema.shape
    },
    async (args) => {
      try {
        const result = await service.saveConversation(args);
        return jsonResult(result);
      } catch (err) {
        return errorResult(`save_conversation failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  );

  const transport = new StdioServerTransport();
  await server.connect(transport);
  // Log to stderr only — stdout is the MCP JSON-RPC channel.
  process.stderr.write(`llmnesia MCP server ready (corpus: ${root})\n`);
}

// Only auto-run when this file is executed directly (`tsx src/mcp.ts` /
// `node dist/mcp.js`), not when `runServe` is imported by cli.ts.
const isMain = import.meta.url === `file://${process.argv[1]}`;
if (isMain) {
  runServe().catch((err) => {
    process.stderr.write(`Fatal: ${err instanceof Error ? err.stack ?? err.message : String(err)}\n`);
    process.exitCode = 1;
  });
}
